import {
  useCreateQuizMutation,
  useGetElearningQuery,
  useGetQuestionsQuery,
  useGetQuizQuery,
  useUpdateQuizMutation,
} from '@/modules/services';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  FormContainer,
  FormItem,
  Input,
  Modal,
  NativeSelect,
  Radio,
  RadioGroup,
  RemoveRedEyeOutlinedIcon,
  Select,
  Text,
  Toggle,
} from 'elmo-elements';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useParams } from 'react-router-dom';
import { ElearningParamType } from '@/pages/Module/routes';
import {
  generateQuestionAskedErrorMessage,
  isCompulsoryQuestionCountEnough,
  isEnabledQuestionCountEnough,
  openPlayerUIWindow,
} from '@/lib/util';
import { appData } from '@/lib/data';
import { FailBehaviour } from '@/modules/types/choice';
import { breakdownQuizMetadataValues } from '@/components/Player/FrameContent/util';
import { useFlags } from 'launchdarkly-react-client-sdk';

const schema = yup.object().shape({
  name: yup.string().max(255).required('Enter a quiz name'),
  questionsAsked: yup
    .number()
    .min(0, 'Value must be >= 0')
    .integer('Must be a whole number'),
  passMark: yup
    .number()
    .integer('Must be a whole number')
    .min(0, 'Must be between 1 - 100')
    .max(100, 'Must be between 1 - 100'),
  correctFeedback: yup.number().min(0).max(1),
  showAnswer: yup.number().min(0).max(1),
  scormHideRedo: yup.number().min(0).max(1),
  responseTracking: yup.number().min(0).max(1),
  attemptLimit: yup.number().min(1).max(10),
  failBehaviour: yup.number().min(0).max(2),
  retryPeriodType: yup.number().min(0).max(1),
  retryPeriodValue: yup.number().min(0).integer('Must be a whole number'),
});

export const defaultQuizValues = {
  name: 'Quiz name',
  questionsAsked: 1,
  passMark: 100,
  correctFeedback: 1,
  showAnswer: 1,
  scormHideRedo: 0,
  compulsoryNo: 0,
  responseTracking: 0,
  attemptLimit: 1,
  failBehaviour: FailBehaviour.restart,
  retryPeriodValue: 0,
  retryPeriodType: 0,
};

export interface IQuizSetup {
  name: string;
  compulsoryNo: number;
  questionsAsked: number;
  passMark: number;
  correctFeedback: string | number;
  showAnswer: string | number;
  scormHideRedo: string | number;
  responseTracking: string | number;
  attemptLimit: number;
  failBehaviour: number;
  retryPeriodType: number;
  retryPeriodValue: number;
}

const retryOptions = [
  {
    value: '0',
    label: 'Hours',
  },
  {
    value: '1',
    label: 'Days',
  },
];

const QuizSetup = (): React.ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { elearningId } = useParams<ElearningParamType>();
  const { learningElaQuizRetryLimit20250128: isQuizAttemptEnabled } =
    useFlags();
  const elearning = useGetElearningQuery(elearningId);
  const { data: quizData } = useGetQuizQuery(elearningId);
  const { data: questionsData } = useGetQuestionsQuery(
    {
      quizId: `${quizData?.id}`,
    },
    {
      skip: !quizData?.id || !quizData,
    },
  );
  const [updateQuiz, updateQuizStatus] = useUpdateQuizMutation();
  const [createQuiz, createQuizStatus] = useCreateQuizMutation();

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IQuizSetup>({
    resolver: yupResolver(schema),
    defaultValues: breakdownQuizMetadataValues(quizData, defaultQuizValues),
  });

  const failBehaviour = watch('failBehaviour');
  const responseTracking = watch('responseTracking');

  const enabledQuestionCount = useMemo(
    () =>
      questionsData?.filter(
        ({ questionMetadata }) => questionMetadata.disabled !== 1,
      )?.length ?? 0,
    [questionsData],
  );

  const compulsoryQuestionCount = useMemo(
    () =>
      questionsData?.filter(
        ({ questionMetadata }) =>
          questionMetadata.compulsory === 1 && questionMetadata.disabled === 0,
      )?.length ?? 0,
    [questionsData],
  );

  useEffect(() => {
    if (quizData) {
      const quiz = breakdownQuizMetadataValues(quizData, defaultQuizValues);

      reset(quiz);
    }
  }, [reset, quizData]);

  const handleChange = (key: keyof IQuizSetup, value: any) => {
    setValue(key, value);
  };

  const onSubmit = (data: IQuizSetup) => {
    const isEnoughQuestionsAsked =
      isEnabledQuestionCountEnough({
        offset: 0,
        enabledQuestionCount,
        questionAsked: Number(watch('questionsAsked')),
      }) &&
      isCompulsoryQuestionCountEnough({
        offset: 0,
        compulsoryQuestionCount,
        questionAsked: Number(watch('questionsAsked')),
      });

    if (!isEnoughQuestionsAsked) {
      handleChange('questionsAsked', quizData?.quizMetadata.questionNo);
      setIsModalOpen(true);
      return;
    }

    if (quizData) {
      const submitData = {
        ...quizData,
        name: data.name,
        quizMetadata: {
          ...quizData?.quizMetadata,
          compulsoryNo: data.compulsoryNo,
          questionNo: data.questionsAsked,
          pass: data.passMark,
          showCorrect: +data.showAnswer,
          showAnswerFeedback: +data.correctFeedback,
          lmsTrackingEnable: +data.responseTracking,
          attemptLimit: +data.attemptLimit,
          failBehaviour: +data.failBehaviour,
          retryPeriodType: +data.retryPeriodType,
          retryPeriodValue: +data.retryPeriodValue,
        },
        scormFeedbackMetadata: {
          ...quizData.scormFeedbackMetadata,
          hideRedoBtn: +data.scormHideRedo,
        },
      };

      updateQuiz(submitData);
    }
  };

  const dispatch = useDispatch();

  useEffect(() => {
    const isUpdateSuccess =
      updateQuizStatus.isSuccess &&
      !updateQuizStatus.isError &&
      updateQuizStatus.data;

    const isCreateSuccess =
      createQuizStatus.isSuccess &&
      !createQuizStatus.isError &&
      createQuizStatus.data;

    if (isUpdateSuccess || isCreateSuccess) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Quiz setup Saved/ Updated Successfully',
        },
      });
    }

    if (updateQuizStatus.isError || createQuizStatus.isError) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
        },
      });
    }
  }, [updateQuizStatus, createQuizStatus]);

  return (
    <>
      <Modal
        type="small"
        isOpened={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title="Not enough questions"
        closeLabel="Close"
        primaryButton={
          <Button
            type="primary"
            isBlock={true}
            onClick={() => setIsModalOpen(false)}
          >
            Ok, got it.
          </Button>
        }
      >
        {Number(watch('questionsAsked')) === 0 ? (
          <>
            Question cannot be empty to update a quiz.
            <br />
            <br />
            Set it to one or more and try again.
          </>
        ) : (
          <>
            You haven't added enough questions to display the amount you would
            like to include in the quiz.
            <br />
            <br />
            Add more questions and try again.
          </>
        )}
      </Modal>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormContainer maxColumns={2}>
          <FormItem
            label="Quiz Name"
            message={errors?.name?.message}
            status={errors.name ? 'error' : 'default'}
            isBlock={true}
          >
            <Input
              {...register('name', {})}
              onChange={(e) =>
                handleChange(e.target.name as keyof IQuizSetup, e.target.value)
              }
              value={watch('name')}
              ariaLabel="Quiz name"
              placeholder="[Quiz Name]"
              id="elearning-quiz-name"
            />
          </FormItem>
          <FormItem size="sm">
            <FormItem
              label="Question Asked"
              message={
                !isEnabledQuestionCountEnough({
                  offset: 0,
                  enabledQuestionCount,
                  questionAsked: Number(watch('questionsAsked')),
                }) ||
                !isCompulsoryQuestionCountEnough({
                  offset: 0,
                  compulsoryQuestionCount,
                  questionAsked: Number(watch('questionsAsked')),
                })
                  ? generateQuestionAskedErrorMessage({
                      questionAsked: Number(watch('questionsAsked')),
                      compulsoryQuestionCount,
                      questionDataCount: questionsData?.length,
                    })
                  : errors.questionsAsked?.message
              }
              status={
                errors.questionsAsked ||
                !isEnabledQuestionCountEnough({
                  offset: 0,
                  enabledQuestionCount,
                  questionAsked: Number(watch('questionsAsked')),
                }) ||
                !isCompulsoryQuestionCountEnough({
                  offset: 0,
                  compulsoryQuestionCount,
                  questionAsked: Number(watch('questionsAsked')),
                })
                  ? 'error'
                  : 'default'
              }
              size="sm"
            >
              <Input
                htmlType="number"
                {...register('questionsAsked', {})}
                onChange={(e) =>
                  handleChange(
                    e.target.name as keyof IQuizSetup,
                    e.target.value,
                  )
                }
                value={watch('questionsAsked')}
                ariaLabel="Quiz question asked"
                id="elearning-question-asked"
              />
            </FormItem>
            <span>
              Amount of questions included in quiz. Selected at random.
            </span>
          </FormItem>

          <FormItem
            label="Pass Mark"
            message={errors.passMark?.message}
            status={errors.passMark ? 'error' : 'default'}
            size="sm"
          >
            <Input
              htmlType="number"
              {...register('passMark', {})}
              onChange={(e) =>
                handleChange(e.target.name as keyof IQuizSetup, e.target.value)
              }
              value={watch('passMark')}
              ariaLabel="Quiz pass mark"
              placeholder="100"
              inputAdornmentEnd="%"
              id="elearning-pass-mark"
            />
          </FormItem>

          <FormItem
            label="Correct/Incorrect Feedback"
            message={errors.correctFeedback?.message}
            status={errors.correctFeedback ? 'error' : 'default'}
            size="sm"
          >
            <NativeSelect
              {...register('correctFeedback', {})}
              value={watch('correctFeedback')}
              onChange={(e) => handleChange('correctFeedback', e)}
              id="elearning-answer-feedback"
              ariaLabel="Answer feedback"
            >
              <NativeSelect.Option label="On" value={1} />
              <NativeSelect.Option label="Off" value={0} />
            </NativeSelect>
          </FormItem>

          <FormItem
            label="Show correct answer"
            message={errors.showAnswer?.message}
            status={errors.showAnswer ? 'error' : 'default'}
            size="sm"
          >
            <NativeSelect
              {...register('showAnswer', {})}
              value={watch('showAnswer')}
              onChange={(e) => handleChange('showAnswer', e)}
              id="elearning-show-answer"
              ariaLabel="Show answer"
            >
              <NativeSelect.Option label="Yes" value={1} />
              <NativeSelect.Option label="No" value={0} />
            </NativeSelect>
          </FormItem>

          <FormItem
            label="Hide redo button"
            message={errors.scormHideRedo?.message}
            status={errors.scormHideRedo ? 'error' : 'default'}
            size="sm"
          >
            <NativeSelect
              {...register('scormHideRedo', {})}
              value={watch('scormHideRedo')}
              onChange={(e) => handleChange('scormHideRedo', e)}
              id="elearning-response-tracking"
              ariaLabel="Response tracking"
            >
              <NativeSelect.Option label="Yes" value={1} />
              <NativeSelect.Option label="No" value={0} />
            </NativeSelect>
          </FormItem>
        </FormContainer>

        {isQuizAttemptEnabled && <Divider />}

        {isQuizAttemptEnabled && (
          <FormContainer maxColumns={2} className="mt-5 mb-4">
            <FormItem
              label=""
              message={errors?.responseTracking?.message}
              status={errors.responseTracking ? 'error' : 'default'}
              isBlock={true}
            >
              <div className="quiz-attempt-container">
                <Text htmlTag="strong" className="restrict-label">
                  Restrict quiz attempts
                </Text>
                <Toggle
                  isOn={+responseTracking > 0}
                  onChange={(e) => {
                    const isChecked = e.target.checked ? 1 : 0;
                    setValue('responseTracking', isChecked);
                  }}
                >
                  <div className="quiz-attempt-toggle">
                    <Toggle.Button />
                    <Toggle.On>
                      <Text className="toggle-text">Enabled</Text>
                    </Toggle.On>
                    <Toggle.Off>
                      <Text className="toggle-text">Disabled</Text>
                    </Toggle.Off>
                  </div>
                </Toggle>
              </div>
            </FormItem>

            {!!responseTracking && (
              <FormItem
                label="Attempts"
                message={errors.attemptLimit?.message}
                status={errors.attemptLimit ? 'error' : 'default'}
                size="sm"
              >
                <NativeSelect
                  {...register('attemptLimit', {})}
                  value={watch('attemptLimit')}
                  onChange={(e) => handleChange('attemptLimit', e)}
                  ariaLabel="Quiz attemptLimit"
                >
                  {Array.from(Array(10).keys()).map((value) => {
                    const newValue = value + 1;

                    return (
                      <NativeSelect.Option
                        key={value}
                        label={`${newValue}`}
                        value={newValue}
                      />
                    );
                  })}
                </NativeSelect>
              </FormItem>
            )}

            {!!responseTracking && (
              <FormItem
                label="Behaviour on Fail"
                message={errors.failBehaviour?.message}
                status={errors.failBehaviour ? 'error' : 'default'}
                isBlock={true}
              >
                <RadioGroup
                  selected={failBehaviour}
                  direction="vertical"
                  onChange={(e) => {
                    handleChange('failBehaviour', e);
                  }}
                >
                  {Object.keys(FailBehaviour)
                    .filter((item) => Number.isNaN(Number(item)))
                    .map((enumKey, index) => (
                      <Radio
                        key={enumKey}
                        className="fail-behaviour-options"
                        value={index}
                      >
                        {enumKey}
                      </Radio>
                    ))}
                </RadioGroup>
                <div className="quiz-retry-options">
                  <FormItem
                    label="Amount"
                    message={errors.retryPeriodValue?.message}
                    status={errors.retryPeriodValue ? 'error' : 'default'}
                    size="sm"
                  >
                    <Input
                      isDisabled={+failBehaviour !== FailBehaviour.retry}
                      htmlType="number"
                      {...register('retryPeriodValue', {})}
                      onChange={(e) =>
                        handleChange(
                          e.target.name as keyof IQuizSetup,
                          e.target.value,
                        )
                      }
                      value={watch('retryPeriodValue')}
                      ariaLabel="Quiz question asked"
                      id="elearning-question-asked"
                    />
                  </FormItem>
                  <FormItem
                    label="Until"
                    message={errors.retryPeriodType?.message}
                    status={errors.retryPeriodType ? 'error' : 'default'}
                    size="sm"
                  >
                    <Select
                      isDisabled={+failBehaviour !== FailBehaviour.retry}
                      value={retryOptions[watch('retryPeriodType')]}
                      options={retryOptions}
                      closeMenuOnSelect={true}
                      onChange={(e: any) => {
                        handleChange('retryPeriodType', +e.value);
                      }}
                    />
                  </FormItem>
                </div>
              </FormItem>
            )}
          </FormContainer>
        )}

        <br />

        <Button
          type="primary"
          htmlType="submit"
          ariaLabel="Submit Form"
          isUppercase={false}
          isLoading={createQuizStatus.isLoading || updateQuizStatus.isLoading}
          className="px-5"
        >
          Save
        </Button>
        <Button
          type="default"
          ariaLabel="Preview quiz"
          isUppercase={false}
          className="ml-2"
          onClick={() => {
            if (elearning.data) {
              openPlayerUIWindow(
                elearning.data,
                `/preview-quiz/${elearning.data.id}`,
                'preview',
                appData.playerTheme.default.windowMetadata,
              );
            }
          }}
        >
          <RemoveRedEyeOutlinedIcon className="preview-icon" />
          Preview Quiz
        </Button>
      </form>
    </>
  );
};

export default QuizSetup;
